/**
 * ★ WA — RESERVA da conversa para a SDR do lead. Puro.
 *
 * A conversa nasce em AGUARDANDO (`responsavelId: null` — `agruparPorEstado` não ganha estado novo), porém
 * RESERVADA para a SDR que o rodízio sorteou, por `config.reservaMinutos` (padrão 10). No prazo, só ela
 * atende; o gestor sempre pode (é quem desempata); depois do prazo, qualquer SDR puxa — e leva o lead junto.
 *
 * Esta é a regra de LEITURA. A autoridade é a rule de `conversas` (`request.time < reservadaAte`); a tela usa
 * isto para não oferecer um botão que o servidor vai recusar, e o `donoAoAgir` para não dar a conversa a quem
 * respondeu por cima da reserva.
 *
 * ★ ESPELHO: `functions/messaging/reserva.js`.
 */

/** Decisão do dono: 10 minutos. Configurável em Configurações → Atendimento. */
export const RESERVA_PADRAO_MIN = 10;

/** Timestamp do Firestore, Date ou texto → Date; senão `null`. */
const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Minutos de reserva da clínica: inteiro positivo, senão o padrão. */
export function minutosDeReserva(config) {
  const v = Math.trunc(Number(config?.reservaMinutos));
  return Number.isFinite(v) && v > 0 ? v : RESERVA_PADRAO_MIN;
}

/** Há reserva valendo AGORA? O instante exato do fim já expirou (a borda pertence a quem chega depois). */
export function reservaVigente(conversa, agora = new Date()) {
  if (!String(conversa?.reservadaParaId ?? '').trim()) return false;
  const ate = paraData(conversa?.reservadaAte);
  return ate !== null && ate.getTime() > new Date(agora).getTime();
}

/** Quanto falta, em minutos inteiros para cima (0 quando não há reserva vigente). */
export function minutosRestantes(conversa, agora = new Date()) {
  if (!reservaVigente(conversa, agora)) return 0;
  const ms = paraData(conversa.reservadaAte).getTime() - new Date(agora).getTime();
  return Math.max(0, Math.ceil(ms / 60000));
}

/**
 * ★★★ PC-6 R6 — o PRAZO em texto curto. Até 60 min: "12 min". Acima (a reserva da noite, D1, vai
 * até o início do expediente + 1 h): "até 09:00" no mesmo dia, "até seg 09:00" em outro — sem
 * isso o cartão mostraria "· 780 min". ⚠ Hora no fuso da clínica (`Intl`), nunca `getHours()`.
 */
export function prazoCurto(conversa, agora = new Date(), fuso = 'America/Sao_Paulo') {
  const min = minutosRestantes(conversa, agora);
  if (min <= 60) return `${min} min`;
  const fmt = (d) => {
    const p = {};
    for (const x of new Intl.DateTimeFormat('en-CA', {
      timeZone: fuso, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d)) p[x.type] = x.value;
    return { dia: `${p.year}-${p.month}-${p.day}`, hora: `${String(Number(p.hour) % 24).padStart(2, '0')}:${p.minute}` };
  };
  const ate = fmt(paraData(conversa.reservadaAte));
  if (ate.dia === fmt(new Date(agora)).dia) return `até ${ate.hora}`;
  const [a, m, d] = ate.dia.split('-').map(Number);
  return `até ${['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][new Date(Date.UTC(a, m - 1, d)).getUTCDay()]} ${ate.hora}`;
}

/** Quem pode atender: sem reserva vigente, todos; com reserva, a reservada ou o gestor. */
export function podeAtender(conversa, usuario, agora = new Date()) {
  if (!reservaVigente(conversa, agora)) return true;
  if (usuario?.papel === 'gestor') return true;
  return Boolean(usuario?.id) && usuario.id === conversa.reservadaParaId;
}

/** O aviso no cartão — sem ele a reserva vira armadilha: a SDR perde o lead por não saber que o tinha. */
export function rotuloDaReserva(conversa, usuario, agora = new Date()) {
  if (!reservaVigente(conversa, agora)) return '';
  const quem = usuario?.id && usuario.id === conversa.reservadaParaId ? 'você' : conversa.reservadaParaNome || 'outra pessoa';
  return `Reservada para ${quem} · ${prazoCurto(conversa, agora)}`;
}
